import {
  Component,
  DestroyRef,
  inject,
  Inject,
  OnInit,
  ViewChild,
  AfterViewInit,
  signal,
  ElementRef,
  ChangeDetectorRef,
  ComponentRef,
} from '@angular/core';
import {
  ASK_MODAL_DATA,
  ModalRef,
  RichtextToolboxDirective,
  ThemeService,
  ToastService,
  ToolKeys,
} from 'asksuite-citrus';
import {
  AnswerTemplateForm,
  AnswerTemplateManageModalFacade,
} from '../../services/answer-template-manage-modal.facade';
import { Company, Translator, TRANSLATOR } from '@livechat/shared';
import { filterSelectedList } from '../../../../../../shared/helpers/filter.helper';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { AnswerTemplateDto } from '../../domain/dtos';
import { ControlsOf } from '@livechat/shared';
import { ArrayValidators } from '../../../../../../shared/validators/array.validator';
import { dtoToFormValue, formValueToDto } from '../../adapters/form.adapter';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoggerService } from '../../../../../../core/services/logger.service';
import { LogEvent } from '../../../../../../core/enums/log-event';
import {
  livechatTemplatesCancelCreateAdapter,
  livechatTemplatesCancelEditAdapter,
  livechatTemplatesSaveCreateAdapter,
  livechatTemplatesSaveEditAdapter,
} from '../../../../../../modules/live-chat/services/logger-adapters';
import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { EmojiPickerComponent } from 'src/app/shared/components/emoji-picker/emoji-picker.component';
import { EmojiSuggestion } from 'src/app/shared/components/emoji-suggestion/emoji-suggestion.interface';
import { EmojiService } from '@core/services/emoji.service';
import { HandleKeyPressService } from 'src/app/shared/services/handle-key-press/handle-key-press.service';
import { LocalizationService } from '@core/services/localization.service';
import { delay, filter } from 'rxjs';

export type AnswerTemplateManageModalParams = {
  attendanceId: string;
  template?: Partial<AnswerTemplateDto>;
};

export type AnswerTemplateManageModalCloseData = {
  shouldRefresh?: boolean;
};

@Component({
  selector: 'app-answer-template-manage-modal',
  templateUrl: './answer-template-manage-modal.component.html',
  styleUrls: ['./answer-template-manage-modal.component.scss'],
})
export class AnswerTemplateManageModalComponent
  implements OnInit, AfterViewInit
{
  @ViewChild(RichtextToolboxDirective, { static: false })
  private richtextToolbox?: RichtextToolboxDirective;
  @ViewChild('emojiSuggestions')
  private emojiSuggestion!: ElementRef<HTMLElement>;

  protected readonly ANSWER_MAX_LENGTH = 3500;
  protected readonly COUNT_ALERT = 1000;
  protected remainingAnswerCharacters = this.ANSWER_MAX_LENGTH;
  protected characterCounterTooltip:
    | 'FORMS.X_CHARACTERS_REMAINING'
    | 'FORMS.NO_CHARACTERS_REMAINING'
    | 'FORMS.1_CHARACTER_REMAINING' = 'FORMS.X_CHARACTERS_REMAINING';

  protected companies!: Array<Company>;
  formGroup!: FormGroup;
  protected loading = false;
  protected isEdit = false;
  protected modalTitle = '';
  protected richtext = {
    tools: ['bold', 'italic', 'list_numbered', 'list_bulleted'] as ToolKeys,
  };

  public exceededLimit = false;
  public showAlert = false;
  public countCharacters = 0;

  private destroyRef = inject(DestroyRef);
  private inputCursorPosition = 0;
  private emojiComponent: ComponentRef<EmojiPickerComponent> | null = null;
  public isEmojiPickerVisible = signal(false);
  protected isFocused = false;
  protected selectedEmojiIndex = 0;
  public emojisSuggestionItem = signal<EmojiSuggestion[]>([]);
  public isEmojiSuggestionsVisible = signal<boolean>(false);
  public colonsToChange = signal<string>('');
  public isDarkMode = signal<boolean>(false);

  constructor(
    @Inject(TRANSLATOR) private translate: Translator,
    @Inject(ASK_MODAL_DATA) private data: AnswerTemplateManageModalParams,
    private modalRef: ModalRef,
    private facade: AnswerTemplateManageModalFacade,
    private formBuilder: FormBuilder,
    private toast: ToastService,
    private logger: LoggerService,
    private overlay: Overlay,
    private emojiService: EmojiService,
    private handleKeyPressService: HandleKeyPressService,
    private cdr: ChangeDetectorRef,
    private themeService: ThemeService,
    private localizationService: LocalizationService
  ) {}

  ngOnInit() {
    this.loadCompanies();
    this.buildForm();
  }

  ngAfterViewInit() {
    this.updateCharacterCounterTooltip();
    this.listenFocusInput();
    this.chooseLanguageEmoji();
    this.listenEmojiSuggestionArrowKeyPress();
    this.listenEmojiSuggestionEnterOrTabKeyPress();
    this.listenThemeChange();
    this.listenEscDown();
  }

  private listenEscDown() {
    this.handleKeyPressService
      .getSpecificKeys(['Escape'])
      .pipe(takeUntilDestroyed(this.destroyRef), delay(0))
      .subscribe((event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        if (this.isEmojiPickerVisible()) {
          setTimeout(() => {
            this.closeEmojiPicker();
            this.richtextToolbox?.focus();
          });
        }
        if (this.isEmojiSuggestionsVisible()) {
          setTimeout(() => {
            this.closeEmojiSuggestion();
            this.richtextToolbox?.focus();
          });
        }
      });
  }

  private chooseLanguageEmoji() {
    if (this.localizationService.selectedLanguage) {
      this.emojiService.chooseLanguageEmoji(
        this.localizationService.selectedLanguage
      );
    }
  }

  private listenThemeChange() {
    this.isDarkMode.set(this.themeService.getTheme() === 'dark');

    this.themeService.listenThemeChange().subscribe((theme) => {
      this.isDarkMode.set(theme === 'dark');
    });
  }

  private listenEmojiSuggestionArrowKeyPress() {
    this.handleKeyPressService
      .getSpecificKeys(['ArrowUp', 'ArrowDown'])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(() => {
          const hasEmojiSuggestion = this.emojisSuggestionItem().length > 0;
          const emojiSuggestionOpen = this.isEmojiSuggestionsVisible();
          return hasEmojiSuggestion && emojiSuggestionOpen;
        })
      )
      .subscribe((event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const lastItemIndex = this.emojisSuggestionItem().length - 1;
        this.selectedEmojiIndex +=
          event.key.toLocaleLowerCase() === 'arrowup' ? -1 : 1;

        if (this.selectedEmojiIndex < 0)
          this.selectedEmojiIndex = lastItemIndex;
        else if (this.selectedEmojiIndex > lastItemIndex)
          this.selectedEmojiIndex = 0;

        setTimeout(() => {
          const element =
            this.emojiSuggestion.nativeElement.querySelectorAll('.emoji-item')[
              this.selectedEmojiIndex
            ];
          element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      });
  }

  private listenEmojiSuggestionEnterOrTabKeyPress() {
    this.handleKeyPressService
      .getSpecificKeys(['Enter', 'Tab'])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        delay(0),
        filter(() => {
          const hasEmojiSuggestion = this.emojisSuggestionItem().length > 0;
          const emojiSuggestionOpen = this.isEmojiSuggestionsVisible();
          return hasEmojiSuggestion && emojiSuggestionOpen;
        })
      )
      .subscribe((event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        this.selectEmojiSuggestion(
          this.emojisSuggestionItem()[this.selectedEmojiIndex].emoji
        );
        this.cdr.detectChanges();
      });
  }

  private listenFocusInput() {
    setTimeout(() => {
      const topToolbox = document.querySelector('.top-toolbox');
      if (!topToolbox) {
        return;
      }

      const firstButton = topToolbox.querySelector('button');
      if (!firstButton) {
        return;
      }

      this.observeButtonState(firstButton as HTMLButtonElement);

      this.isFocused = !(firstButton as HTMLButtonElement).disabled;
    }, 10);
  }

  private observeButtonState(element: HTMLButtonElement) {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'disabled'
        ) {
          const target = mutation.target as HTMLButtonElement;
          this.isFocused = !target.disabled;
        }
      });
    });

    observer.observe(element, {
      attributes: true,
      attributeFilter: ['disabled'],
    });

    this.destroyRef.onDestroy(() => observer.disconnect());
  }

  selectEmojiSuggestion(emoji: any) {
    const text = this.formGroup.get('content')?.value || '';
    const newText = this.emojiService.replaceEmojiInText(
      text,
      this.colonsToChange(),
      emoji
    );
    const cursor = this.emojiService.getNewCursorPosition(
      this.inputCursorPosition,
      emoji.length,
      this.colonsToChange.length
    );

    this.formGroup.get('content')?.setValue(newText, { emitEvent: false });
    this.richtextToolbox?.setCursorPosition(cursor);
    this.closeEmojiSuggestion();
  }

  public closeEmojiSuggestion() {
    this.isEmojiSuggestionsVisible.set(false);
  }

  public openEmojiSuggestion() {
    this.isEmojiPickerVisible.set(false);
    this.isEmojiSuggestionsVisible.set(true);
  }

  public handleEmojiSuggestionCommand(text: string) {
    const foundMatch = this.emojiService.handleEmojiSuggestionCommand({
      text,
      inputCursorPosition: this.inputCursorPosition,
    });

    if (!foundMatch) {
      if (this.isEmojiSuggestionsVisible()) this.closeEmojiSuggestion();
      return;
    }

    this.colonsToChange.set(foundMatch[0]);
    const emojis = this.emojiService.searchSuggestion({
      emojiTarget: foundMatch[1],
    });

    if (emojis) {
      this.emojisSuggestionItem.set(emojis);
      if (!this.isEmojiSuggestionsVisible()) this.openEmojiSuggestion();
    }
  }

  public onEmojiPickerOpen(event: MouseEvent) {
    const targetElement = event.currentTarget as HTMLElement;

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(targetElement)
      .withPositions([
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
          offsetY: -5,
          offsetX: -203,
        },
      ]);

    const overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: 'emoji-picker-backdrop',
      panelClass: 'emoji-overlay-panel',
    });
    this.isEmojiPickerVisible.set(true);

    overlayRef.backdropClick().subscribe(() => {
      overlayRef.dispose();
      this.isEmojiPickerVisible.set(false);
    });

    this.emojiComponent = overlayRef.attach(
      new ComponentPortal(EmojiPickerComponent)
    );

    this.emojiComponent.instance.isDarkMode = this.isDarkMode();
    this.emojiComponent.instance.isVisible = this.isEmojiPickerVisible();
    this.emojiComponent.instance.type = 'wide';

    this.emojiComponent.instance.emojiSelected.subscribe((event: any) => {
      this.addEmoji(event);
    });

    this.emojiComponent.instance.closed.subscribe(() => {
      overlayRef.dispose();
      this.isEmojiPickerVisible.set(false);
    });
  }

  public onFocusSuggestion(index: number) {
    this.selectedEmojiIndex = index;
  }

  public onFocus() {
    const position = this.richtextToolbox?.getCurrentCursorPosition();
    this.inputCursorPosition = position?.index || 0;

    this.handleEmojiSuggestionCommand(this.formGroup.get('content')?.value);
  }

  public onInputChange() {
    const position = this.richtextToolbox?.getCurrentCursorPosition();
    this.inputCursorPosition = position?.index || 0;
  }

  private loadCompanies() {
    this.companies =
      this.facade.getCompanies().filter((c) => c.companyId && c.companyName) ||
      [];
  }

  private buildForm() {
    this.formGroup = this.formBuilder.group<ControlsOf<AnswerTemplateForm>>({
      idAnswerTemplate: new FormControl(null),
      title: new FormControl('', [Validators.required]),
      companiesIds: new FormControl(
        [],
        [Validators.required, ArrayValidators.min(1)]
      ),
      content: new FormControl('', [Validators.required]),
    });

    this.listenContentChange();

    if (this.data?.template) {
      const newValue = dtoToFormValue(this.data.template, this.companies);

      this.isEdit = !!newValue.idAnswerTemplate;
      this.modalTitle = this.isEdit
        ? 'EDIT_ANSWER_TEMPLATE'
        : 'NEW_ANSWER_TEMPLATE';

      this.formGroup.patchValue(newValue);
      this.formGroup
        .get('content')
        ?.updateValueAndValidity({ emitEvent: true });
    }
  }

  private listenContentChange() {
    this.formGroup
      .get('content')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((newValue) => {
        this.updateCharacterCounterTooltip();
        this.handleEmojiSuggestionCommand(newValue);
      });
  }

  private updateCharacterCounterTooltip() {
    if (!this.richtextToolbox) return;
    this.countCharacters = this.richtextToolbox!.getTextLength()! - 1;
    this.remainingAnswerCharacters =
      this.ANSWER_MAX_LENGTH - this.countCharacters;
    this.exceededLimit = this.countCharacters > this.ANSWER_MAX_LENGTH;
    this.showAlert = this.countCharacters >= this.COUNT_ALERT;
    switch (this.remainingAnswerCharacters) {
      case 0:
        this.characterCounterTooltip = 'FORMS.NO_CHARACTERS_REMAINING';
        break;
      case 1:
        this.characterCounterTooltip = 'FORMS.1_CHARACTER_REMAINING';
        break;
      default:
        this.characterCounterTooltip = 'FORMS.X_CHARACTERS_REMAINING';
        break;
    }
  }

  public close(data?: AnswerTemplateManageModalCloseData) {
    this.modalRef.close(data);
  }

  public addEmoji(event: { emoji: { native: string } }) {
    const emoji = event.emoji.native;
    const cursor = this.inputCursorPosition + emoji.length;
    const truePosition =
      this.formGroup.get('content')?.value.length === 0
        ? 0
        : this.inputCursorPosition;
    this.richtextToolbox?.insertText(truePosition, emoji, true);
    this.richtextToolbox?.setCursorPosition(cursor);
  }

  public closeEmojiPicker() {
    this.isEmojiPickerVisible.set(false);
    this.emojiComponent?.instance.closed.emit();
    this.emojiComponent?.destroy();
  }
  protected save() {
    this.loading = true;

    if (this.formGroup.invalid) {
      this.toast.info(this.translate.key('FORMS.ALL_FIELDS_MUST_BE_FILLED'));
      this.loading = false;
      return;
    }

    if (this.isEdit) {
      this.facade.update(this.formGroup.value).subscribe({
        next: this.onSaveSuccess.bind(this),
        error: this.onSaveError.bind(this),
      });
    } else {
      this.facade.save(this.formGroup.value).subscribe({
        next: this.onSaveSuccess.bind(this),
        error: this.onSaveError.bind(this),
      });
    }
  }

  private onSaveSuccess(template: AnswerTemplateDto) {
    this.loading = false;
    this.toast.success(
      this.translate.key(
        this.isEdit ? 'UPDATED_SUCCESSFULLY' : 'ANSWER_TEMPLATE_CREATED_SUCCESS'
      )
    );
    this.close({ shouldRefresh: true });
  }

  private onSaveError(template: AnswerTemplateDto) {
    this.loading = false;
    this.toast.danger(
      this.translate.key(
        this.isEdit
          ? 'ANSWER_TEMPLATE_UPDATED_ERROR'
          : 'ANSWER_TEMPLATE_CREATED_ERROR'
      )
    );
  }

  protected displayFn = (company: Company) => company.companyName;

  protected filterFn = (search: string) =>
    filterSelectedList(this.companies, search, ['companyName', 'companyId']);

  protected logCancelClick() {
    const logEvent = this.isEdit
      ? LogEvent.LIVECHAT_TEMPLATES_CANCEL_EDIT
      : LogEvent.LIVECHAT_TEMPLATES_CANCEL_CREATE;

    const data = this.isEdit
      ? livechatTemplatesCancelEditAdapter(
          this.data.attendanceId,
          this.data.template?.idAnswerTemplate || 0
        )
      : livechatTemplatesCancelCreateAdapter(this.data.attendanceId);

    this.logger.log(logEvent, data);
  }

  protected logSaveClick() {
    const logEvent = this.isEdit
      ? LogEvent.LIVECHAT_TEMPLATES_SAVE_EDIT
      : LogEvent.LIVECHAT_TEMPLATES_SAVE_CREATE;

    const data = this.isEdit
      ? livechatTemplatesSaveEditAdapter(
          this.data.attendanceId,
          formValueToDto(this.formGroup.value)
        )
      : livechatTemplatesSaveCreateAdapter(
          this.data.attendanceId,
          formValueToDto(this.formGroup.value)
        );

    this.logger.log(logEvent, data);
  }

  getClassDarkLight() {
    const theme = document.body.className;
    return theme === 'dark' ? true : false;
  }
}
